import {
    Box,
    Button,
    Flex,
    Grid,
    Menu,
    MenuButton,
    MenuItemOption,
    MenuList,
    MenuOptionGroup,
    Skeleton,
    Stack,
    Text,
    useToast
} from '@chakra-ui/react'
import { Icon, Pagination } from 'components/atoms'
import { useAuthContext } from 'contexts/auth'
import { useInstitutionsContext } from 'contexts/institutions'
import jwt_decode from 'jwt-decode'
import { AuthTokens } from 'models'
import React, { useEffect, useRef, useState } from 'react'
import {
    deleteNotification,
    getListNotifications,
    visualizeNotification
} from 'services/notifications'
import formatter from 'utils/formatter'
import { removeEmptyValues } from 'utils/removeEmptyValues'
import { FilterNotifications } from '../FilterNotifications'
import { FormFilters } from '../FilterNotifications/interfaces'
import { Notification, PreviewRef } from '../intefaces'
import NoNotifications from '../NoNotification'
import { NotificationItem } from '../NotificationItem'
import { NotificationPreview } from '../NotificationPreview'

interface ParamsNotification {
    page: number
    limit: number
    filters?: FormFilters
}

const NotificationList: React.FC = () => {
    const [currentPage, setCurrentPage] = useState<number>(1)
    const [totalPages, setTotalPages] = useState<number>(0)
    const [selectedNotification, setSelectedNotification] =
        useState<Notification>({} as Notification)
    const { currentInstitution } = useInstitutionsContext()
    const [limit, setLimit] = useState<number>(10)

    const { currentUser, isBackoffice } = useAuthContext()
    const [filtersState, setFiltersState] = useState<FormFilters>(
        {} as FormFilters
    )

    const [notifications, setNotifications] = useState<Notification[]>([])
    const [loading, setLoading] = useState<boolean>(true)

    const refPreview = useRef<PreviewRef>(null)
    const toast = useToast()
    const handleMarkAsRead = (guid: string) => {
        setNotifications(
            notifications.map(notif =>
                notif.guid === guid ? { ...notif, lida: true } : notif
            )
        )

        fetchToogleNotificationStatus(guid)
    }

    const onSelect = (notification: Notification) => {
        setSelectedNotification(notification)
        handleMarkAsRead(notification.guid)
        refPreview?.current?.openPreview()
    }

    const getIdUser = (): string => {
        const decodedIdToken =
            currentUser && (jwt_decode(currentUser?.idToken) as AuthTokens)
        const idUser = decodedIdToken!['custom:usuario_guid']

        return idUser
    }

    const fetchToogleNotificationStatus = (guid: string) => {
        if (isBackoffice) return
        visualizeNotification(getIdUser(), [guid])
    }

    const fetchRemoveNotification = async (guid: string) => {
        try {
            await deleteNotification(getIdUser(), guid)

            toast({
                title: 'Notificação',
                description: 'Notificação removida com sucesso.',
                duration: 1000,
                status: 'success'
            })

            fetchNotification({
                page: 1,
                limit
            })
        } catch (e) {
            toast({
                title: 'Falha ao apagar notificação',
                description: 'Tente novamente mais tarde.',
                duration: 1000,
                status: 'error'
            })
        }
    }

    const onRemove = (guid: string) => {
        fetchRemoveNotification(guid)
    }

    const onRefreshPage = () => {
        fetchNotification({
            page: 1,
            limit
        })
    }

    const onPageChange = (page: number) => {
        if (page !== currentPage) {
            setCurrentPage(page)
            fetchNotification({
                page,
                limit
            })
        }
    }

    const getFilters = (filtersSearch?: FormFilters) => {
        const filters = removeEmptyValues(filtersSearch || filtersState)

        if (filters.dataInicial) {
            filters.dataInicial = formatter.formatDateToPayload(
                filters.dataInicial
            )
        }

        if (filters.dataFinal) {
            filters.dataFinal = formatter.formatDateToPayload(filters.dataFinal)
        }

        return filters
    }

    const fetchNotification = async ({
        page,
        limit,
        filters
    }: ParamsNotification) => {
        setLoading(true)

        try {
            const response = await getListNotifications(getIdUser(), {
                pagina: page,
                limite: limit,
                ...getFilters(filters),
                instituicaoFinanceira: currentInstitution?.id
            })

            setTotalPages(+response?.headers['total-pages'])
            setNotifications(response.data)
        } catch (error) {
            console.error('fetch API Notifications ->', error)
        } finally {
            setLoading(false)
        }
    }
    useEffect(() => {
        if (currentInstitution?.id !== undefined) {
            fetchNotification({
                page: 1,
                limit: 10
            })
        }
    }, [currentInstitution?.id])

    const onSubmitFilters = (filters: FormFilters) => {
        setFiltersState(filters)

        fetchNotification({
            page: 1,
            limit: 10,
            filters
        })
    }

    if (
        notifications.length < 1 &&
        !loading &&
        Object.keys(filtersState).length === 0
    ) {
        return <NoNotifications onRefreshPage={onRefreshPage} />
    }

    const limits = ['5', '10', '20', '30', '40', '50']

    const handleLimit = (limit: number) => {
        setLimit(limit)
        setCurrentPage(1)
        fetchNotification({
            page: 1,
            limit
        })
    }

    return (
        <Grid templateColumns={{ base: '1fr', md: '1fr' }} gap={4}>
            <Box>
                <FilterNotifications onSubmit={onSubmitFilters} />

                {!loading ? (
                    <Stack spacing={0} borderRadius="lg">
                        {notifications.length > 0 ? (
                            notifications.map(notification => (
                                <NotificationItem
                                    key={notification.guid}
                                    onSelect={onSelect}
                                    notification={notification}
                                    onRemove={onRemove}
                                />
                            ))
                        ) : (
                            <Box
                                display="flex"
                                flexDirection="column"
                                justifyContent="center"
                                alignItems="center"
                                height="300px"
                                margin="24px 0"
                                background="linear-gradient(to right, #f8f9fa, #ffffff)"
                                boxShadow="0 4px 12px rgba(0, 0, 0, 0.08)"
                                borderRadius="12px"
                                padding="24px"
                                border="1px solid #edf2f7"
                            >
                                <Text
                                    fontSize="20px"
                                    fontWeight="500"
                                    color="#4A5568"
                                    textAlign="center"
                                >
                                    Nenhuma notificação encontrada.
                                </Text>
                                <Text
                                    fontSize="14px"
                                    color="#718096"
                                    marginTop="8px"
                                    textAlign="center"
                                >
                                    Tente ajustar seus filtros ou fazer uma nova
                                    busca.
                                </Text>
                            </Box>
                        )}
                    </Stack>
                ) : (
                    <Grid templateColumns={{ base: '1fr', md: '1fr' }} gap={4}>
                        <Skeleton height="36px" width="100%" />
                        <Skeleton height="36px" width="100%" />
                        <Skeleton height="36px" width="100%" />
                        <Skeleton height="36px" width="100%" />
                        <Skeleton height="36px" width="100%" />
                        <Skeleton height="36px" width="100%" />
                        <Skeleton height="36px" width="100%" />
                    </Grid>
                )}

                {totalPages > 1 && (
                    <Flex justify="center" align="center" mt={4} gap={2}>
                        <Menu matchWidth>
                            {({ isOpen }) => (
                                <>
                                    <MenuButton
                                        as={Button}
                                        colorScheme="off-white"
                                        color="inherit"
                                        size="md"
                                        shadow="md"
                                        gap={2}
                                        rightIcon={
                                            <Icon
                                                name={
                                                    isOpen
                                                        ? 'chevron-down'
                                                        : 'chevron-up'
                                                }
                                            />
                                        }
                                    >
                                        {limit}
                                    </MenuButton>
                                    <MenuList
                                        border={0}
                                        shadow="md"
                                        minW="inherit"
                                        position="sticky"
                                        zIndex={12}
                                    >
                                        <MenuOptionGroup
                                            type="radio"
                                            border={0}
                                            shadow="md"
                                            defaultValue="10"
                                            onChange={value =>
                                                handleLimit(Number(value))
                                            }
                                        >
                                            {limits.map(limit => (
                                                <MenuItemOption
                                                    fontSize={16}
                                                    fontWeight={500}
                                                    key={`pagination-limit-${limit}`}
                                                    value={limit}
                                                >
                                                    {limit}
                                                </MenuItemOption>
                                            ))}
                                        </MenuOptionGroup>
                                    </MenuList>
                                </>
                            )}
                        </Menu>

                        <Pagination
                            py={4}
                            pages={totalPages}
                            currentPage={currentPage}
                            changePage={onPageChange}
                        />
                    </Flex>
                )}
            </Box>

            <NotificationPreview
                notification={selectedNotification}
                ref={refPreview}
            />
        </Grid>
    )
}

export default NotificationList
